import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../../services/api';
import { KGInstance } from '../../types';
import '../shared.css';
import './MemoryManager.css';

interface MemoryManagerProps {
  kgId: string;
  versionId: string;
}

// ─── API response shapes (matches backend get_instance_metrics) ───────────────
//
// Response envelope: data.attributes → MetricsAttributes
//
// live       ← gds.systemMonitor()        fields: freeHeap, totalHeap, maxHeap,
//                                          jvmAvailableCpuCores, jvmHeapStatus,
//                                          ongoingGdsProcedures
// system     ← gds.debug.sysInfo()        fields: totalPhysicalMemoryInBytes,
//                                          freePhysicalMemoryInBytes, availableCPUs,
//                                          neo4jVersion, gdsVersion, vmVersion
// heap       ← dbms.queryJmx(Memory)      fields: heap_max_bytes, heap_used_bytes,
//                                          heap_committed_bytes, non_heap_used_bytes
// neo4j_config ← dbms.listConfig()        { name → value } dict
// graph_stats  ← apoc.meta.stats()        node_count, rel_count, labels {}
// indexes    ← SHOW INDEXES + db.stats    { summary: {…}, items: […] }
// transactions ← SHOW TRANSACTIONS        { total, items: […] }
// gds_jobs   ← gds.listProgress()         { total, items: […] }

interface Live {
  free_heap_bytes: number | null;
  total_heap_bytes: number | null;
  max_heap_bytes: number | null;
  available_cpu_cores: number | null;
  heap_status?: { freeHeap?: string; totalHeap?: string; maxHeap?: string } | null;
  ongoing_gds_procedures?: number | null;
}

interface Heap {
  heap_max_bytes: number | null;
  heap_used_bytes: number | null;
  heap_committed_bytes: number | null;
  non_heap_used_bytes: number | null;
}

interface SystemInfo {
  total_physical_memory_bytes: number | null;
  free_physical_memory_bytes: number | null;
  available_cpus: number | null;
  neo4j_version?: string | null;
  gds_version?: string | null;
  jvm_version?: string | null;
}

interface GraphStats {
  node_count: number | null;
  rel_count: number | null;
  labels: Record<string, number> | null;
}

interface IndexEntry {
  id: number | null;
  name: string;
  type: string;
  state: string;
  labels_or_types: string[] | null;
  properties: string[] | null;
  size_bytes: number | null;        // estimated bytes (proportional from node counts)
  entry_count: number | null;       // entry count from db.stats GRAPH COUNTS
  population_percent: number | null;
}

interface IndexesPayload {
  summary: { total: number; online: number; populating: number; failed: number };
  items: IndexEntry[];
}

interface TxEntry {
  transaction_id: string | null;
  current_query: string | null;
  status: string | null;
  elapsed_time: string | null;
  allocated_bytes: number | null;
  page_hits: number | null;
  page_faults: number | null;
}

interface TxPayload {
  total: number;
  items: TxEntry[];
}

interface GdsJob {
  job_id: string | null;
  task_name: string | null;
  progress: string | number | null;
  status: string | null;
}

interface GdsPayload {
  total: number;
  items: GdsJob[];
}

interface PageCache {
  hit_ratio: number | null;       // 0–1
  usage_ratio: number | null;     // 0–1
  flushes: number | null;
  evictions: number | null;
}

interface StoreSize {
  total_store_size: number | null;
  node_store_size: number | null;
  rel_store_size: number | null;
  property_store_size: number | null;
  string_store_size: number | null;
  array_store_size: number | null;
}

interface GdsGraph {
  graph_name: string;
  node_count: number | null;
  rel_count: number | null;
  memory_usage: string | null;
  creation_time: string | null;
}

interface QueryLogEntry {
  query: string;
  invocation_count: number;
  avg_exec_us: number;        // µs
  max_exec_us: number;
  avg_compile_us: number;
  first_invoked_ms: number | null;  // epoch ms — earliest invocation
  last_invoked_ms: number | null;   // epoch ms — most recent invocation
}

interface MetricsAttributes {
  instance_info?: { instance_type?: string | null };
  live?: Live;
  system?: SystemInfo;
  heap?: Heap;
  neo4j_config?: Record<string, string | null>;
  graph_stats?: GraphStats;
  indexes?: IndexesPayload;
  transactions?: TxPayload;
  gds_jobs?: GdsPayload;
  page_cache?: PageCache;
  store_size?: StoreSize;
  gds_graphs?: GdsGraph[];
  query_log?: QueryLogEntry[];
}

// ─── Utilities ───────────────────────────────────────────────────────────────

const GIB = 1073741824;
const MIB = 1048576;

function fmtBytes(n: number | null | undefined): { value: string; unit: string } {
  if (n == null || isNaN(n) || n < 0) return { value: '—', unit: '' };
  if (n >= GIB) return { value: (n / GIB).toFixed(2), unit: 'GiB' };
  if (n >= MIB) return { value: (n / MIB).toFixed(1), unit: 'MiB' };
  if (n >= 1024) return { value: (n / 1024).toFixed(1), unit: 'KiB' };
  return { value: String(n), unit: 'B' };
}

function fmtBytesStr(n: number | null | undefined): string {
  const { value, unit } = fmtBytes(n);
  return unit ? `${value} ${unit}` : value;
}

function fmtCount(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '—';
  return n.toLocaleString();
}

function heapPct(used: number, max: number): number {
  if (!max || max <= 0) return 0;
  return Math.min(100, (used / max) * 100);
}

function pctVariant(pct: number): 'good' | 'medium' | 'warn' {
  if (pct > 85) return 'warn';
  if (pct > 70) return 'medium';
  return 'good';
}

function fmtMicros(us: number): string {
  if (us <= 0) return '0µs';
  if (us < 1000) return `${us.toFixed(0)}µs`;
  if (us < 1_000_000) return `${(us / 1000).toFixed(1)}ms`;
  return `${(us / 1_000_000).toFixed(2)}s`;
}

function fmtTimestamp(ms: number | null): string {
  if (!ms) return '—';
  const d = new Date(ms);
  const date = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `${date}, ${time}`;
}


function parseIsoDuration(val: string | null | undefined): number {
  if (!val) return 0;
  const m = String(val).match(/PT?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+\.?\d*)S)?/i);
  if (!m) return 0;
  return parseFloat(m[1] || '0') * 3600 + parseFloat(m[2] || '0') * 60 + parseFloat(m[3] || '0');
}

function fmtDuration(val: string | null | undefined): string {
  const s = parseIsoDuration(val);
  if (s === 0) return '0s';
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.floor(s % 60)}s`;
}

// Handles the executeSynchronousQuery response shape: { data: Record<string,any>[] }
function parseQueryResult(res: any): Record<string, any>[] {
  if (!res) return [];
  // Primary shape: { data: [...] }
  if (Array.isArray(res.data)) return res.data;
  // Fallback: res itself is an array
  if (Array.isArray(res)) return res;
  // Fallback: { data: { attributes: { result: [...] } } }
  const attrs = res?.data?.attributes;
  if (attrs) {
    const raw = attrs?.result ?? attrs?.results ?? attrs?.data;
    if (Array.isArray(raw)) return raw;
  }
  return [];
}

function formatInstanceLabel(inst: KGInstance): string {
  const type = inst.instance_type || 'instance';
  const tail = inst.instance_id.slice(-10);
  const suffix = inst.default_instance ? ' · default' : '';
  return `${type} · …${tail}${suffix}`;
}

// ─── Small atoms ─────────────────────────────────────────────────────────────

const GaugeBar: React.FC<{ pct: number }> = ({ pct }) => {
  const v = pctVariant(pct);
  return (
    <div className="mm-gauge-track">
      <div className={`mm-gauge-fill mm-gauge-fill--${v}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const TxStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`mm-tx-badge mm-tx-badge--${(status || 'unknown').toLowerCase()}`}>{status || '—'}</span>
);

// ─── Reusable info tooltip ────────────────────────────────────────────────────

const InfoTip: React.FC<{ tip: string }> = ({ tip }) => (
  <span className="mm-kpi-tip-wrap">
    <span className="mm-kpi-tip-icon">ⓘ</span>
    <span className="mm-kpi-tip-bubble">{tip}</span>
  </span>
);

// ─── KPI card ────────────────────────────────────────────────────────────────

const KpiCard: React.FC<{
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  accent?: 'purple' | 'green' | 'blue' | 'orange';
  tip?: string;
}> = ({ label, value, unit, sub, accent = 'purple', tip }) => (
  <div className={`mm-kpi mm-kpi--${accent}`}>
    <div className="mm-kpi-label">
      {label}
      {tip && (
        <span className="mm-kpi-tip-wrap">
          <span className="mm-kpi-tip-icon">ⓘ</span>
          <span className="mm-kpi-tip-bubble">{tip}</span>
        </span>
      )}
    </div>
    <div className="mm-kpi-value">
      {value}
      {unit && <span className="mm-kpi-unit">{unit}</span>}
    </div>
    <div className="mm-kpi-sub">{sub ?? '\u00a0'}</div>
  </div>
);

// ─── Memory panel (left middle card) ─────────────────────────────────────────

const MemoryPanel: React.FC<{
  live?: Live;
  heap?: Heap;
  sysInfo?: SystemInfo;
  pageCache?: PageCache;
}> = ({ live, heap, sysInfo, pageCache }) => {
  // Prefer JMX heap (more accurate); fall back to live monitor if JMX unavailable
  const jvmUsed  = heap?.heap_used_bytes ?? null;
  const jvmMax   = heap?.heap_max_bytes ?? null;
  const jvmPct   = (jvmUsed != null && jvmMax != null && jvmMax > 0)
    ? heapPct(jvmUsed, jvmMax)
    : (live?.total_heap_bytes != null && live?.free_heap_bytes != null && live?.max_heap_bytes != null)
      ? heapPct(live.total_heap_bytes - live.free_heap_bytes, live.max_heap_bytes)
      : null;
  const jvmUsedVal = jvmUsed ?? (live && live.total_heap_bytes != null && live.free_heap_bytes != null
    ? live.total_heap_bytes - live.free_heap_bytes : null);
  const jvmMaxVal  = jvmMax ?? live?.max_heap_bytes ?? null;

  // Non-heap: JVM metaspace + code cache — no hard max, show as gauge against a 1 GiB reference
  const nonHeapUsed = heap?.non_heap_used_bytes ?? null;
  const NON_HEAP_REF = 1 * GIB; // visual reference ceiling
  const nonHeapPct = nonHeapUsed != null ? Math.min(100, (nonHeapUsed / NON_HEAP_REF) * 100) : null;

  // Page cache: usage ratio (how full) and hit ratio (how effective)
  const pcUsagePct = pageCache?.usage_ratio != null ? pageCache.usage_ratio * 100 : null;
  const pcHitPct   = pageCache?.hit_ratio   != null ? pageCache.hit_ratio   * 100 : null;

  const hasAny = jvmPct !== null || nonHeapPct !== null || pcUsagePct !== null;

  return (
    <div className="mm-panel mm-panel--memory">
      <div className="mm-panel-title">Memory</div>

      {!hasAny && <div className="mm-unavail">Unavailable</div>}

      {/* JVM Heap */}
      {jvmPct !== null && (
        <div className="mm-mem-block">
          <div className="mm-mem-block-label">
            <span>JVM Heap <InfoTip tip="Java heap memory used by Neo4j. High % (>85%) causes GC pressure and slowdowns. Configured via server.memory.heap.max_size." /></span>
            <span className={`mm-mem-pct mm-mem-pct--${pctVariant(jvmPct)}`}>{jvmPct.toFixed(0)}%</span>
          </div>
          <GaugeBar pct={jvmPct} />
          <div className="mm-mem-nums">
            <span>{fmtBytesStr(jvmUsedVal)} used</span>
            <span>{fmtBytesStr(jvmMaxVal)} max</span>
          </div>
        </div>
      )}

      {/* Non-Heap — metaspace + code cache */}
      {nonHeapPct !== null && (
        <div className="mm-mem-block">
          <div className="mm-mem-block-label">
            <span>Non-Heap <InfoTip tip="JVM metaspace and code cache — stores class metadata and JIT-compiled code. Not part of the heap max; grows independently." /></span>
            <span className="mm-mem-pct mm-mem-pct--good">{fmtBytesStr(nonHeapUsed)}</span>
          </div>
          <GaugeBar pct={nonHeapPct} />
          <div className="mm-mem-nums">
            <span>Metaspace + code cache</span>
          </div>
        </div>
      )}

      {/* Page Cache */}
      {pcUsagePct !== null && (
        <div className="mm-mem-block">
          <div className="mm-mem-block-label">
            <span>Page Cache <InfoTip tip="OS-level cache for graph store files. High usage (>90%) with low hit ratio means Neo4j is reading from disk — increase server.memory.pagecache.size to improve query speed." /></span>
            <span className={`mm-mem-pct mm-mem-pct--${pctVariant(pcUsagePct)}`}>{pcUsagePct.toFixed(0)}% full</span>
          </div>
          <GaugeBar pct={pcUsagePct} />
          <div className="mm-mem-nums">
            <span>Hit ratio: <strong>{pcHitPct != null ? `${pcHitPct.toFixed(1)}%` : '—'}</strong></span>
            {pageCache?.evictions != null && <span>{pageCache.evictions.toLocaleString()} evictions</span>}
          </div>
        </div>
      )}

    </div>
  );
};

// ─── Graph panel (right middle card) ─────────────────────────────────────────

const GraphPanel: React.FC<{ graph?: GraphStats | null }> = ({ graph }) => {
  const labels = graph?.labels ? Object.entries(graph.labels).sort((a, b) => b[1] - a[1]) : [];
  const maxCount = labels.length > 0 ? labels[0][1] : 1;

  return (
    <div className="mm-panel mm-panel--graph">
      <div className="mm-panel-title">Graph (Counts)</div>

      {!graph || labels.length === 0 ? (
        <div className="mm-unavail">Unavailable</div>
      ) : (
        <div className="mm-label-list">
          {labels.map(([label, count]) => (
            <div key={label} className="mm-label-row">
              <span className="mm-label-name">{label}</span>
              <div className="mm-label-bar-track">
                <div
                  className="mm-label-bar-fill"
                  style={{ width: `${(count / maxCount) * 100}%` }}
                />
              </div>
              <span className="mm-label-count">{count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Query Log tab ────────────────────────────────────────────────────────────

type LogSortCol = 'last_invoked_ms' | 'max_exec_us' | 'avg_compile_us';

const PAGE_SIZE = 100;

// Format a Date to "YYYY-MM-DDTHH:mm" in LOCAL time — required by datetime-local inputs
function toLocalDateTimeStr(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const Paginator: React.FC<{ total: number; page: number; onPage: (p: number) => void }> = ({ total, page, onPage }) => {
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return null;
  const start = page * PAGE_SIZE + 1;
  const end   = Math.min((page + 1) * PAGE_SIZE, total);
  return (
    <div className="mm-paginator">
      <button className="mm-page-btn" disabled={page === 0} onClick={() => onPage(page - 1)}>‹ Prev</button>
      <span className="mm-page-info">{start}–{end} of {total}</span>
      <button className="mm-page-btn" disabled={page >= totalPages - 1} onClick={() => onPage(page + 1)}>Next ›</button>
    </div>
  );
};


const QueryLogTab: React.FC<{ queryLog: QueryLogEntry[] }> = ({ queryLog }) => {
  const nowStr     = () => toLocalDateTimeStr(new Date());
  const weekAgoStr = () => toLocalDateTimeStr(new Date(Date.now() - 7 * 86_400_000));

  // From/To inputs — pre-filled with sensible defaults so inputs aren't empty
  const [fromStr,       setFromStr]       = useState<string>(() => weekAgoStr());
  const [toStr,         setToStr]         = useState<string>(() => nowStr());
  const [searchInput,   setSearchInput]   = useState('');
  // Applied values drive the filter — start with no restriction so all queries show
  const [appliedFrom,   setAppliedFrom]   = useState<string>('');
  const [appliedTo,     setAppliedTo]     = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sortCol, setSortCol] = useState<LogSortCol>('last_invoked_ms');
  const [sortAsc, setSortAsc] = useState(false); // default: newest first
  const [page,    setPage]    = useState(0);

  const handleNow = () => setToStr(nowStr());

  const applyFilters = () => {
    setAppliedFrom(fromStr);
    setAppliedTo(toStr);
    setAppliedSearch(searchInput);
    setPage(0);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') applyFilters();
  };

  const toggleSort = (col: LogSortCol) => {
    if (sortCol === col) setSortAsc(v => !v);
    else { setSortCol(col); setSortAsc(false); }
    setPage(0);
  };

  const fromMs = appliedFrom ? new Date(appliedFrom).getTime() : null;
  const toMs   = appliedTo   ? new Date(appliedTo).getTime()   : null;

  const filtered = queryLog
    .filter(e => {
      if (fromMs && e.last_invoked_ms != null && e.last_invoked_ms < fromMs) return false;
      if (toMs   && e.last_invoked_ms != null && e.last_invoked_ms > toMs)   return false;
      if (appliedSearch && !e.query.toLowerCase().includes(appliedSearch.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const av = a[sortCol] ?? 0;
      const bv = b[sortCol] ?? 0;
      return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });

  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="mm-qlog-wrap">
      {/* ── Filter bar ── */}
      <div className="mm-qlog-filters">
        <div className="mm-qlog-filter-group">
          <label className="mm-qlog-filter-label">From</label>
          <input
            type="datetime-local"
            className="mm-qlog-date-input"
            value={fromStr}
            onChange={e => setFromStr(e.target.value)}
          />
        </div>
        <div className="mm-qlog-filter-group">
          <label className="mm-qlog-filter-label">To</label>
          <div className="mm-qlog-date-row">
            <input
              type="datetime-local"
              className="mm-qlog-date-input"
              value={toStr}
              onChange={e => setToStr(e.target.value)}
            />
            <button className="mm-now-btn" onClick={handleNow}>Now</button>
          </div>
        </div>
        <div className="mm-qlog-filter-group mm-qlog-filter-grow">
          <label className="mm-qlog-filter-label">Query text</label>
          <input
            type="text"
            className="mm-qlog-search-input"
            placeholder="Filter by keyword…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={onSearchKeyDown}
          />
        </div>
        <div className="mm-qlog-filter-group">
          <label className="mm-qlog-filter-label mm-qlog-count-label">
            {filtered.length === queryLog.length
              ? `${queryLog.length} queries`
              : `${filtered.length} / ${queryLog.length}`}
          </label>
          <button className="mm-search-btn" onClick={applyFilters}>Search</button>
        </div>
      </div>

      {/* ── Table ── */}
      {filtered.length === 0 ? (
        <div className="mm-empty-inline">No queries match the selected range</div>
      ) : (
        <>
          <div className="mm-table-wrap">
            <table className="mm-table">
              <colgroup>
                <col style={{ width: '130px' }} />
                <col style={{ width: '82px' }} />
                <col style={{ width: '82px' }} />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th className="mm-th-sort" onClick={() => toggleSort('last_invoked_ms')}>
                    Run At {sortCol === 'last_invoked_ms' ? (sortAsc ? '↑' : '↓') : <span className="mm-sort-inactive">↕</span>}
                    <InfoTip tip="Date and time this query was last executed." />
                  </th>
                  <th className="mm-th-sort" onClick={() => toggleSort('max_exec_us')}>
                    Exec Time {sortCol === 'max_exec_us' ? (sortAsc ? '↑' : '↓') : <span className="mm-sort-inactive">↕</span>}
                    <InfoTip tip="Execution time excluding query planning. Orange when > 1s." />
                  </th>
                  <th className="mm-th-sort" onClick={() => toggleSort('avg_compile_us')}>
                    Compile {sortCol === 'avg_compile_us' ? (sortAsc ? '↑' : '↓') : <span className="mm-sort-inactive">↕</span>}
                    <InfoTip tip="Time Neo4j spent planning this query. High = plan not yet cached." />
                  </th>
                  <th>Query <InfoTip tip="Click ▶ to expand the full Cypher query." /></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((entry, i) => {
                  const isSlow = entry.max_exec_us > 1_000_000;
                  const preview = entry.query.length > 120 ? entry.query.slice(0, 120) + '…' : entry.query;
                  return (
                    <tr key={page * PAGE_SIZE + i} className={isSlow ? 'mm-tr-warn' : ''}>
                      <td className="mm-td-mono">{fmtTimestamp(entry.last_invoked_ms)}</td>
                      <td className={`mm-td-mono${isSlow ? ' mm-td-warn' : ''}`}>{fmtMicros(entry.max_exec_us)}</td>
                      <td className="mm-td-mono">{fmtMicros(entry.avg_compile_us)}</td>
                      <td className="mm-td-query-col">
                        <details className="mm-query-details">
                          <summary className="mm-query-summary">{preview}</summary>
                          <span className="mm-query-full">{entry.query}</span>
                        </details>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Paginator total={filtered.length} page={page} onPage={setPage} />
        </>
      )}
    </div>
  );
};

// ─── Bottom table panel ───────────────────────────────────────────────────────

type BottomTab = 'transactions' | 'indexes' | 'gds' | 'logs';

const BottomPanel: React.FC<{
  txPayload?: TxPayload;
  indexesPayload?: IndexesPayload;
  gdsPayload?: GdsPayload;
  gdsGraphs?: GdsGraph[];
  queryLog?: QueryLogEntry[];
  tab: BottomTab;
  setTab: (t: BottomTab) => void;
  onDropGraph?: (graphName: string) => Promise<void>;
}> = ({ txPayload, indexesPayload, gdsPayload, gdsGraphs, queryLog, tab, setTab, onDropGraph }) => {

  const transactions = txPayload?.items ?? [];
  const indexes = indexesPayload?.items ?? [];
  const gdsJobs = gdsPayload?.items ?? [];
  const graphs = gdsGraphs ?? [];

  const [txPage,  setTxPage]  = useState(0);
  const [idxPage, setIdxPage] = useState(0);
  const [gdsPage, setGdsPage] = useState(0);
  const [droppingGraph, setDroppingGraph] = useState<string | null>(null);
  const [dropConfirm, setDropConfirm] = useState<string | null>(null);

  // sort by entry_count desc (proxy for size)
  const indexesBySize = [...indexes].sort((a, b) => {
    const aE = a.entry_count ?? -1;
    const bE = b.entry_count ?? -1;
    return bE - aE;
  });
  const totalEntries = indexes.reduce((s, i) => s + (i.entry_count ?? 0), 0);

  const txRows  = transactions.slice(txPage  * PAGE_SIZE, (txPage  + 1) * PAGE_SIZE);
  const idxRows = indexesBySize.slice(idxPage * PAGE_SIZE, (idxPage + 1) * PAGE_SIZE);
  const gdsGraphRows = graphs.slice(gdsPage * PAGE_SIZE, (gdsPage + 1) * PAGE_SIZE);
  const gdsJobRows   = gdsJobs.slice(0, PAGE_SIZE); // jobs rarely exceed 100

  return (
    <div className="mm-bottom-panel">
      <div className="mm-bottom-tabs">
        <button
          className={`mm-bottom-tab${tab === 'logs' ? ' mm-bottom-tab--active' : ''}`}
          onClick={() => setTab('logs')}
        >
          Query Log
          {queryLog && queryLog.length > 0 && (
            <span className="mm-tab-count">{queryLog.length}</span>
          )}
        </button>
        <button
          className={`mm-bottom-tab${tab === 'transactions' ? ' mm-bottom-tab--active' : ''}`}
          onClick={() => setTab('transactions')}
        >
          Transactions
          <span className={`mm-tab-count${transactions.length > 5 ? ' mm-tab-count--danger' : ''}`}>
            {transactions.length}
          </span>
        </button>
        <button
          className={`mm-bottom-tab${tab === 'indexes' ? ' mm-bottom-tab--active' : ''}`}
          onClick={() => setTab('indexes')}
        >
          Index Sizes
          <span className="mm-tab-count">{indexes.length}</span>
        </button>
        <button
          className={`mm-bottom-tab${tab === 'gds' ? ' mm-bottom-tab--active' : ''}`}
          onClick={() => setTab('gds')}
        >
          GDS
          <span className="mm-tab-count">{graphs.length + gdsJobs.length}</span>
        </button>
      </div>

      <div className="mm-bottom-body">

        {tab === 'transactions' && (
          transactions.length === 0 ? (
            <div className="mm-empty-inline">No active transactions</div>
          ) : (
            <>
              <div className="mm-table-wrap">
                <table className="mm-table">
                  <colgroup>
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '80px' }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Transaction ID <InfoTip tip="Unique ID assigned by Neo4j to each open transaction." /></th>
                      <th>Status <InfoTip tip="Current state: RUNNING (actively executing), BLOCKED (waiting for a lock)." /></th>
                      <th>Elapsed <InfoTip tip="Wall-clock time since the transaction was opened. Highlighted in orange when > 30s." /></th>
                      <th>Allocated <InfoTip tip="Heap memory allocated by this transaction so far. Large values indicate memory-intensive queries." /></th>
                      <th>Page Hits <InfoTip tip="Number of times data was found in the page cache (fast). High ratio vs faults = good cache use." /></th>
                      <th>Page Faults <InfoTip tip="Number of times data had to be read from disk. High faults = data not in page cache, causing slow I/O." /></th>
                      <th>Query <InfoTip tip="The Cypher query currently being executed by this transaction." /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {txRows.map((tx, i) => {
                      const sec = parseIsoDuration(tx.elapsed_time);
                      const isLong = sec > 30;
                      return (
                        <tr key={tx.transaction_id ?? i} className={isLong ? 'mm-tr-warn' : ''}>
                          <td className="mm-td-mono">{tx.transaction_id ?? '—'}</td>
                          <td><TxStatusBadge status={tx.status ?? ''} /></td>
                          <td className={`mm-td-mono${isLong ? ' mm-td-warn' : ''}`}>{fmtDuration(tx.elapsed_time)}</td>
                          <td className="mm-td-mono">{fmtBytesStr(tx.allocated_bytes)}</td>
                          <td className="mm-td-mono">{fmtCount(tx.page_hits)}</td>
                          <td className="mm-td-mono">{fmtCount(tx.page_faults)}</td>
                          <td className="mm-td-query-col">
                            <details className="mm-query-details">
                              <summary className="mm-query-summary">
                                {tx.current_query && tx.current_query.length > 120 ? tx.current_query.slice(0, 120) + '…' : (tx.current_query ?? '—')}
                              </summary>
                              {tx.current_query && tx.current_query.length > 120 && (
                                <span className="mm-query-full">{tx.current_query}</span>
                              )}
                            </details>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Paginator total={transactions.length} page={txPage} onPage={setTxPage} />
            </>
          )
        )}

        {tab === 'indexes' && (
          indexes.length === 0 ? (
            <div className="mm-empty-inline">No index size data available</div>
          ) : (
            <>
              <div className="mm-idx-size-summary">
                <strong>{indexes.length} indexes</strong>
                {totalEntries > 0 && <span className="mm-dim"> · {totalEntries.toLocaleString()} total entries · sorted by entry count</span>}
              </div>
              <div className="mm-table-wrap">
                <table className="mm-table">
                  <thead>
                    <tr>
                      <th>Index <InfoTip tip="Internal name of the index as defined in Neo4j." /></th>
                      <th>Type <InfoTip tip="Index implementation: RANGE (default), FULLTEXT (text search), LOOKUP (label/type scans), POINT (spatial)." /></th>
                      <th>Labels / Types <InfoTip tip="The node label or relationship type this index is built on." /></th>
                      <th>Properties <InfoTip tip="The property key(s) indexed. Composite indexes list multiple properties." /></th>
                      <th>State <InfoTip tip="ONLINE = serving queries. POPULATING = still being built. FAILED = needs to be dropped and recreated." /></th>
                      <th>Built <InfoTip tip="% of the index that has been populated. 100% = fully built and serving queries." /></th>
                      <th>Entries <InfoTip tip="Number of indexed entries from db.stats. Larger counts = bigger index on disk. Bar shows relative size." /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {idxRows.map((idx, i) => {
                      const maxEntries = indexesBySize[0]?.entry_count ?? 1;
                      const barPct = idx.entry_count != null && maxEntries > 0
                        ? (idx.entry_count / maxEntries) * 100 : 0;
                      return (
                        <tr key={idx.name || i}>
                          <td className="mm-td-mono mm-td-key">{idx.name}</td>
                          <td className="mm-td-mono">{idx.type || '—'}</td>
                          <td className="mm-td-mono">{(idx.labels_or_types ?? []).join(', ') || '—'}</td>
                          <td className="mm-td-mono">{(idx.properties ?? []).join(', ') || '—'}</td>
                          <td className="mm-td-mono">{idx.state || '—'}</td>
                          <td className="mm-td-mono">
                            {idx.population_percent != null ? `${idx.population_percent.toFixed(0)}%` : '—'}
                          </td>
                          <td>
                            {idx.entry_count != null ? (
                              <div className="mm-idx-size-cell">
                                <div className="mm-idx-size-bar-track">
                                  <div className="mm-idx-size-bar-fill" style={{ width: `${barPct}%` }} />
                                </div>
                                <span className="mm-td-mono">{idx.entry_count.toLocaleString()}</span>
                              </div>
                            ) : <span className="mm-dim">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Paginator total={indexes.length} page={idxPage} onPage={setIdxPage} />
            </>
          )
        )}

        {tab === 'gds' && (
          <div className="mm-gds-wrap">
            {/* projected graphs */}
            <div className="mm-gds-section-title">Projected Graphs</div>
            {graphs.length === 0 ? (
              <div className="mm-empty-inline">No projected graphs in memory</div>
            ) : (
              <>
                <div className="mm-table-wrap">
                  <table className="mm-table">
                    <thead>
                      <tr>
                        <th>Graph Name</th>
                        <th>Nodes</th>
                        <th>Relationships</th>
                        <th>Memory Usage</th>
                        <th>Created</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {gdsGraphRows.map((g, i) => {
                        const isDropping = droppingGraph === g.graph_name;
                        const isConfirming = dropConfirm === g.graph_name;
                        return (
                          <tr key={g.graph_name || i}>
                            <td className="mm-td-mono mm-td-key">{g.graph_name || '—'}</td>
                            <td className="mm-td-mono">{fmtCount(g.node_count)}</td>
                            <td className="mm-td-mono">{fmtCount(g.rel_count)}</td>
                            <td className="mm-td-mono">{g.memory_usage ?? '—'}</td>
                            <td className="mm-td-mono mm-td-ts">
                              {g.creation_time
                                ? new Date(g.creation_time).toLocaleString()
                                : '—'}
                            </td>
                            <td className="mm-td-action">
                              {isConfirming ? (
                                <span className="mm-drop-confirm">
                                  Drop?&nbsp;
                                  <button
                                    className="mm-drop-btn mm-drop-btn--danger"
                                    disabled={isDropping}
                                    onClick={async () => {
                                      if (!onDropGraph) return;
                                      setDroppingGraph(g.graph_name);
                                      setDropConfirm(null);
                                      try { await onDropGraph(g.graph_name); }
                                      finally { setDroppingGraph(null); }
                                    }}
                                  >
                                    {isDropping ? '…' : 'Yes'}
                                  </button>
                                  <button
                                    className="mm-drop-btn"
                                    onClick={() => setDropConfirm(null)}
                                  >No</button>
                                </span>
                              ) : (
                                <button
                                  className="mm-drop-btn mm-drop-btn--ghost"
                                  disabled={!!droppingGraph}
                                  onClick={() => setDropConfirm(g.graph_name)}
                                  title="Drop this projection from memory"
                                >
                                  Drop
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Paginator total={graphs.length} page={gdsPage} onPage={setGdsPage} />
              </>
            )}
            {/* active jobs */}
            <div className="mm-gds-section-title" style={{ marginTop: 16 }}>Active Jobs</div>
            {gdsJobs.length === 0 ? (
              <div className="mm-empty-inline">No active GDS jobs</div>
            ) : (
              <div className="mm-table-wrap">
                <table className="mm-table">
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Task</th>
                      <th>Progress</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gdsJobRows.map((job, i) => {
                      const pct = typeof job.progress === 'number'
                        ? job.progress
                        : typeof job.progress === 'string' && job.progress.endsWith('%')
                          ? parseFloat(job.progress)
                          : null;
                      return (
                        <tr key={job.job_id ?? i}>
                          <td className="mm-td-mono">{job.job_id ?? '—'}</td>
                          <td className="mm-td-mono">{job.task_name ?? '—'}</td>
                          <td>
                            {pct !== null ? (
                              <div className="mm-prog-wrap">
                                <div className="mm-prog-track">
                                  <div className="mm-prog-fill" style={{ width: `${Math.min(100, pct)}%` }} />
                                </div>
                                <span className="mm-prog-label">{pct.toFixed(0)}%</span>
                              </div>
                            ) : (
                              <span className="mm-td-mono">{job.progress ?? '—'}</span>
                            )}
                          </td>
                          <td><TxStatusBadge status={job.status ?? ''} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'logs' && (
          <QueryLogTab queryLog={queryLog ?? []} />
        )}

      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const MemoryManager: React.FC<MemoryManagerProps> = ({ kgId, versionId }) => {
  const [instances, setInstances] = useState<KGInstance[]>([]);
  const instancesRef = useRef<KGInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [loadingInstances, setLoadingInstances] = useState(true);

  // Mounted guard — prevents setState after unmount
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // Generation counter — guards against stale fetchMetrics responses
  const fetchGenRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tailing, setTailing] = useState(false);
  const [data, setData] = useState<MetricsAttributes | null>(null);
  const [error, setError] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [bottomTab, setBottomTab] = useState<BottomTab>('logs'); // Query Log opens first

  // ── Load instances ──────────────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        setLoadingInstances(true);
        const res: any = await apiService.listInstances(kgId, versionId);
        if (!res.data) return;
        const list: KGInstance[] = res.data.map((item: any) => {
          const a = item.attributes ?? item;
          return {
            instance_id: a.instance_id ?? item.id,
            kg_id: a.kg_id ?? kgId,
            version_id: a.version_id ?? versionId,
            instance_type: a.instance_type ?? '',
            status: a.status ?? '',
            is_terminated: a.is_terminated ?? false,
            default_instance: a.default_instance ?? false,
            created_at: a.created_at ?? 0,
            org_id: a.org_id ?? '',
          };
        });
        const running = list.filter(i => ['running', 'active'].includes(i.status.toLowerCase()));
        instancesRef.current = running;
        setInstances(running);
        const def = running.find(i => i.default_instance) ?? running[0];
        if (def) setSelectedInstance(def.instance_id);
      } catch (err: any) {
        setError(err?.message || 'Failed to load instances');
      } finally {
        setLoadingInstances(false);
      }
    })();
  }, [kgId, versionId]);

  // ── Fetch metrics via synchronous Cypher queries ────────────────────────────

  const fetchMetrics = useCallback(async (bg = false) => {
    if (!selectedInstance) return;
    const gen = ++fetchGenRef.current;
    bg ? setRefreshing(true) : setLoading(true);
    setError('');

    const run = (q: string) =>
      apiService.executeSynchronousQuery(kgId, versionId, q, selectedInstance);

    const safe = async (q: string): Promise<Record<string, any>[]> => {
      try {
        const res: any = await run(q);
        return parseQueryResult(res);
      } catch {
        return [];
      }
    };

    try {
      const [sysInfoRows, monitorRows, configRows, indexRows, graphRows, jmxRows, gcRows, txRows, gdsRows, pageCacheRows, storeRows, gdsGraphRows, queryLogRows] =
        await Promise.all([
          safe('CALL gds.debug.sysInfo() YIELD key, value RETURN key, value'),
          safe('CALL gds.systemMonitor() YIELD freeHeap, totalHeap, maxHeap, jvmAvailableCpuCores, jvmHeapStatus, ongoingGdsProcedures RETURN freeHeap, totalHeap, maxHeap, jvmAvailableCpuCores, jvmHeapStatus, ongoingGdsProcedures'),
          safe("CALL dbms.listConfig() YIELD name, value WHERE name IN ['server.memory.heap.initial_size','server.memory.heap.max_size','server.memory.pagecache.size','dbms.memory.transaction.total.max','db.memory.transaction.max'] RETURN name, value"),
          safe('SHOW INDEXES YIELD id, name, type, state, labelsOrTypes, properties, indexProvider, populationPercent RETURN id, name, type, state, labelsOrTypes, properties, indexProvider, populationPercent ORDER BY id'),
          safe("CALL apoc.meta.stats() YIELD nodeCount, relCount, labels RETURN nodeCount, relCount, labels"),
          safe('CALL dbms.queryJmx("java.lang:type=Memory") YIELD attributes RETURN attributes'),
          safe("CALL db.stats.retrieve('GRAPH COUNTS') YIELD data RETURN data"),
          safe('SHOW TRANSACTIONS YIELD transactionId, currentQuery, status, elapsedTime, currentQueryAllocatedBytes, currentQueryPageHits, currentQueryPageFaults RETURN transactionId, currentQuery, status, elapsedTime, currentQueryAllocatedBytes, currentQueryPageHits, currentQueryPageFaults ORDER BY elapsedTime DESC'),
          safe('CALL gds.listProgress() YIELD jobId, taskName, progress, status RETURN jobId, taskName, progress, status'),
          safe('CALL dbms.queryJmx("org.neo4j:instance=kernel#0,name=Page cache") YIELD attributes RETURN attributes'),
          safe('CALL apoc.monitor.store() YIELD totalStoreSize, nodeStoreSize, relStoreSize, propStoreSize, stringStoreSize, arrayStoreSize RETURN totalStoreSize, nodeStoreSize, relStoreSize, propStoreSize, stringStoreSize, arrayStoreSize'),
          safe('CALL gds.graph.list() YIELD graphName, nodeCount, relationshipCount, memoryUsage, creationTime RETURN graphName, nodeCount, relationshipCount, memoryUsage, creationTime'),
          safe("CALL db.stats.retrieve('QUERIES') YIELD data RETURN data"),
        ]);

      // system — gds.debug.sysInfo() returns {key, value} rows
      const sysMap: Record<string, any> = {};
      sysInfoRows.forEach(r => { if (r.key) sysMap[r.key] = r.value; });

      // live — single row from gds.systemMonitor()
      const mon = monitorRows[0] ?? {};

      // heap — JMX attributes
      // Shape: jmxRows[0].attributes.HeapMemoryUsage.value.data.properties.{init,used,committed,max}
      const jmxAttrs = jmxRows[0]?.attributes ?? {};
      const heapProps = jmxAttrs?.HeapMemoryUsage?.value?.data?.properties
                     ?? jmxAttrs?.HeapMemoryUsage?.value?.properties
                     ?? {};
      const nonHeapProps = jmxAttrs?.NonHeapMemoryUsage?.value?.data?.properties
                        ?? jmxAttrs?.NonHeapMemoryUsage?.value?.properties
                        ?? {};

      // neo4j_config
      const neo4j_config: Record<string, string | null> = {};
      configRows.forEach(r => { if (r.name) neo4j_config[r.name] = r.value ?? null; });

      // graph stats — single row from apoc.meta.stats()
      const graphRow = graphRows[0] ?? {};

      // ── Index sizes ──────────────────────────────────────────────────────────
      // Neo4j on this server has no per-index byte API (no sizeInBytes in SHOW INDEXES,
      // no apoc.monitor.store, no JMX store sizes). Best we can do:
      // Use node counts per label (from apoc.meta.stats labels map) as a proxy for
      // relative index sizes — each RANGE index on a label with N nodes ≈ proportional to N.
      const labelCounts: Record<string, number> = graphRow.labels ?? {};

      // Also read entry counts from db.stats GRAPH COUNTS
      const gcEntryLookup: Record<string, number> = {};
      if (gcRows.length > 0) {
        const gcData = gcRows[0]?.data ?? {};
        for (const entry of gcData?.indexes ?? []) {
          const key = [
            ...(entry.labels ?? []).slice().sort(),
            '|',
            ...(entry.properties ?? []).slice().sort(),
          ].join(',');
          gcEntryLookup[key] = entry.totalSize ?? 0;
        }
      }

      const indexItems: IndexEntry[] = indexRows.map(r => {
        const labels = r.labelsOrTypes ?? [];
        const props  = r.properties ?? [];
        const gcKey  = [...labels.slice().sort(), '|', ...props.slice().sort()].join(',');
        const entryCount: number | null = gcEntryLookup[gcKey] ?? null;

        // Use node count for the primary label as size proxy (for display only)
        const nodeCount = labels.length > 0 ? (labelCounts[labels[0]] ?? null) : null;
        const sizeBytes = nodeCount != null ? nodeCount * 50 : null;

        return {
          id: r.id ?? null,
          name: r.name ?? '',
          type: r.type ?? '',
          state: r.state ?? '',
          labels_or_types: labels,
          properties: props,
          size_bytes: sizeBytes,
          entry_count: entryCount,
          population_percent: r.populationPercent ?? null,
        };
      });

      const idxSummary = {
        total: indexItems.length,
        online:     indexItems.filter(i => i.state.toUpperCase() === 'ONLINE').length,
        populating: indexItems.filter(i => i.state.toUpperCase() === 'POPULATING').length,
        failed:     indexItems.filter(i => i.state.toUpperCase() === 'FAILED').length,
      };

      // transactions — filter out the SHOW TRANSACTIONS query itself
      const txItems: TxEntry[] = txRows
        .filter(r => !String(r.currentQuery ?? '').toUpperCase().startsWith('SHOW TRANSACTIONS'))
        .map(r => ({
          transaction_id: r.transactionId ?? null,
          current_query: r.currentQuery ?? null,
          status: r.status ?? null,
          elapsed_time: r.elapsedTime != null ? String(r.elapsedTime) : null,
          allocated_bytes: r.currentQueryAllocatedBytes ?? null,
          page_hits: r.currentQueryPageHits ?? null,
          page_faults: r.currentQueryPageFaults ?? null,
        }));

      // gds jobs
      const gdsItems: GdsJob[] = gdsRows.map(r => ({
        job_id: r.jobId ?? null,
        task_name: r.taskName ?? null,
        progress: r.progress ?? null,
        status: r.status ?? null,
      }));

      // page cache — JMX attributes
      const pcAttrs = pageCacheRows[0]?.attributes ?? {};
      const pcHitRatio  = pcAttrs?.HitRatio?.value ?? pcAttrs?.hitRatio?.value ?? null;
      const pcUsageRatio = pcAttrs?.UsageRatio?.value ?? pcAttrs?.usageRatio?.value ?? null;
      const pcFlushes   = pcAttrs?.Flushes?.value ?? null;
      const pcEvictions = pcAttrs?.Evictions?.value ?? null;

      // store size — apoc.monitor.store()
      const storeRow = storeRows[0] ?? {};

      // gds projected graphs
      // creationTime from Neo4j GDS comes back as a temporal object:
      // { year, month, day, hour, minute, second, nanosecond, ... }
      // Normalise it to an ISO string so new Date() works reliably.
      const normaliseNeo4jDatetime = (v: any): string | null => {
        if (v == null) return null;
        if (typeof v === 'string') return v;
        if (typeof v === 'number') return new Date(v).toISOString();
        // Neo4j temporal object shape
        const { year, month, day, hour = 0, minute = 0, second = 0, nanosecond = 0 } = v;
        if (year == null || month == null || day == null) return null;
        const ms = Math.floor(nanosecond / 1_000_000);
        return new Date(Date.UTC(year, month - 1, day, hour, minute, second, ms)).toISOString();
      };

      const gdsGraphItems: GdsGraph[] = gdsGraphRows.map(r => ({
        graph_name:    r.graphName ?? '',
        node_count:    r.nodeCount ?? null,
        rel_count:     r.relationshipCount ?? null,
        memory_usage:  r.memoryUsage ?? null,
        creation_time: normaliseNeo4jDatetime(r.creationTime),
      }));

      // query log — db.stats.retrieve('QUERIES') returns one row per unique query
      // Shape: { data: { query, invocationSummary: { invocationCount, executionTimeInUs:{avg,max}, compileTimeInUs:{avg} }, invocations:[{startTimestampMillis}] } }
      const queryLogItems: QueryLogEntry[] = queryLogRows
        .map(r => {
          const d = r.data ?? r;
          const summary = d.invocationSummary ?? {};
          const invocations: any[] = d.invocations ?? [];
          const timestamps = invocations
            .map((i: any) => i.startTimestampMillis ?? 0)
            .filter((t: number) => t > 0);
          const firstMs = timestamps.length > 0 ? Math.min(...timestamps) : null;
          const lastMs  = timestamps.length > 0 ? Math.max(...timestamps) : null;
          return {
            query:             String(d.query ?? '').trim(),
            invocation_count:  summary.invocationCount ?? 1,
            avg_exec_us:       summary.executionTimeInUs?.avg ?? 0,
            max_exec_us:       summary.executionTimeInUs?.max ?? 0,
            avg_compile_us:    summary.compileTimeInUs?.avg ?? 0,
            first_invoked_ms:  firstMs,
            last_invoked_ms:   lastMs,
          };
        })
        .filter(e => e.query.length > 0)
        .sort((a, b) => (b.last_invoked_ms ?? 0) - (a.last_invoked_ms ?? 0));

      const payload: MetricsAttributes = {
        instance_info: { instance_type: instancesRef.current.find(i => i.instance_id === selectedInstance)?.instance_type ?? null },
        live: {
          free_heap_bytes:    mon.freeHeap ?? null,
          total_heap_bytes:   mon.totalHeap ?? null,
          max_heap_bytes:     mon.maxHeap ?? null,
          available_cpu_cores: mon.jvmAvailableCpuCores ?? null,
          heap_status:        mon.jvmHeapStatus ?? null,
          ongoing_gds_procedures: mon.ongoingGdsProcedures ?? null,
        },
        system: {
          total_physical_memory_bytes: sysMap['totalPhysicalMemoryInBytes'] ?? null,
          free_physical_memory_bytes:  sysMap['freePhysicalMemoryInBytes'] ?? null,
          available_cpus:  sysMap['availableCPUs'] ?? null,
          neo4j_version:   sysMap['neo4jVersion'] ?? null,
          gds_version:     sysMap['gdsVersion'] ?? null,
          jvm_version:     sysMap['vmVersion'] ?? null,
        },
        heap: {
          heap_max_bytes:       heapProps.max ?? null,
          heap_used_bytes:      heapProps.used ?? null,
          heap_committed_bytes: heapProps.committed ?? null,
          non_heap_used_bytes:  nonHeapProps.used ?? null,
        },
        neo4j_config,
        graph_stats: {
          node_count: graphRow.nodeCount ?? null,
          rel_count:  graphRow.relCount ?? null,
          labels:     graphRow.labels ?? null,
        },
        indexes:      { summary: idxSummary, items: indexItems },
        transactions: { total: txItems.length, items: txItems },
        gds_jobs:     { total: gdsItems.length, items: gdsItems },
        page_cache: {
          hit_ratio:    typeof pcHitRatio === 'number' ? pcHitRatio : null,
          usage_ratio:  typeof pcUsageRatio === 'number' ? pcUsageRatio : null,
          flushes:      typeof pcFlushes === 'number' ? pcFlushes : null,
          evictions:    typeof pcEvictions === 'number' ? pcEvictions : null,
        },
        store_size: {
          total_store_size:    storeRow.totalStoreSize ?? null,
          node_store_size:     storeRow.nodeStoreSize ?? null,
          rel_store_size:      storeRow.relStoreSize ?? null,
          property_store_size: storeRow.propStoreSize ?? null,
          string_store_size:   storeRow.stringStoreSize ?? null,
          array_store_size:    storeRow.arrayStoreSize ?? null,
        },
        gds_graphs: gdsGraphItems,
        query_log:  queryLogItems,
      };

      if (gen !== fetchGenRef.current) return;
      if (!mountedRef.current) return;
      setData(payload);
      setLastRefreshed(new Date());
    } catch (err: any) {
      if (gen !== fetchGenRef.current) return;
      if (!mountedRef.current) return;
      setError(err?.message || 'Failed to load metrics');
    } finally {
      if (gen !== fetchGenRef.current) return;
      if (!mountedRef.current) return;
      bg ? setRefreshing(false) : setLoading(false);
    }
  }, [kgId, versionId, selectedInstance]);

  useEffect(() => {
    if (selectedInstance) fetchMetrics();
  }, [selectedInstance]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tailing: refresh every 3 s when enabled ─────────────────────────────────
  useEffect(() => {
    if (!tailing || !selectedInstance) return;
    const id = setInterval(() => fetchMetrics(true), 3_000);
    return () => clearInterval(id);
  }, [tailing, selectedInstance, fetchMetrics]);

  // ── Guards ──────────────────────────────────────────────────────────────────

  if (loadingInstances) {
    return (
      <div className="mm-manager tab-root">
        <div className="tab-loader">
          <span className="tab-spinner" />
        </div>
      </div>
    );
  }

  if (instances.length === 0) {
    return (
      <div className="mm-manager tab-root">
        <div className="tab-empty">
          <div className="tab-empty-icon">⬡</div>
          <div className="tab-empty-title">No running instances</div>
          <div className="tab-empty-body">
            Launch an instance from the <strong>Instances</strong> tab first.
          </div>
        </div>
      </div>
    );
  }

  // ── Derived ─────────────────────────────────────────────────────────────────

  const live    = data?.live;
  const heap    = data?.heap;
  const sysInfo = data?.system;
  const graph   = data?.graph_stats;
  const sysHeader = data?.system;

  const idxItems   = data?.indexes?.items ?? [];
  const txTotal    = data?.transactions?.total ?? 0;
  const pcHit      = data?.page_cache?.hit_ratio;
  const pcUsage    = data?.page_cache?.usage_ratio;
  const storeTotal = data?.store_size?.total_store_size;
  const gdsGraphs  = data?.gds_graphs ?? [];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="mm-manager tab-root">

      {/* ── Top bar ── */}
      <div className="tab-topbar">
        <div className="tab-topbar-left">
          <div className="tab-instance-wrap">
            <span className="tab-instance-dot" />
            <select
              id="mm-inst-sel"
              className="tab-instance-select"
              value={selectedInstance}
              onChange={e => setSelectedInstance(e.target.value)}
            >
              {instances.map(inst => (
                <option key={inst.instance_id} value={inst.instance_id}>
                  {formatInstanceLabel(inst)}
                </option>
              ))}
            </select>
          </div>
          {(sysHeader?.neo4j_version || sysHeader?.gds_version) && (
            <div className="mm-sys-pills">
              {sysHeader?.neo4j_version && <span className="mm-sys-pill">Neo4j {sysHeader.neo4j_version}</span>}
              {sysHeader?.gds_version   && <span className="mm-sys-pill">GDS {sysHeader.gds_version}</span>}
            </div>
          )}
          {lastRefreshed && (
            <span className="tab-stat-pill">Updated {lastRefreshed.toLocaleTimeString()}</span>
          )}
        </div>
        <div className="tab-topbar-right">
          <button
            className={`tab-btn${tailing ? ' tab-btn--primary' : ' tab-btn--ghost'}`}
            disabled={loading}
            onClick={() => setTailing(v => !v)}
          >
            <span className={`mm-tail-dot${tailing ? ' mm-tail-dot--live' : ''}`} />
            {tailing ? 'Stop Tailing' : 'Live'}
          </button>
          <button
            className={`tab-btn tab-btn--ghost${refreshing || loading ? ' mm-refresh-btn--active' : ''}`}
            disabled={loading || refreshing}
            onClick={() => fetchMetrics(true)}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error}</span>
          <button className="tab-banner-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {loading ? (
        <div className="mm-skeleton-wrap">
          <div className="mm-skeleton-kpi-row">
            {[1, 2, 3, 4].map(i => <div key={i} className="mm-skeleton-kpi" />)}
          </div>
          <div className="mm-skeleton-mid-row">
            <div className="mm-skeleton-panel" />
            <div className="mm-skeleton-panel" />
          </div>
          <div className="mm-skeleton-bottom" />
        </div>
      ) : (
        <div className="mm-dashboard">

          {/* ── Row 1: KPI strip ── */}
          <div className="mm-kpi-row">
            {/* graph group */}
            {graph?.node_count != null && (
              <KpiCard label="Nodes" value={fmtCount(graph.node_count)} accent="purple"
                tip="Total number of nodes (entities) stored in this graph database." />
            )}
            {graph?.rel_count != null && (
              <KpiCard label="Relationships" value={fmtCount(graph.rel_count)} accent="purple"
                tip="Total number of relationships (edges) connecting nodes in the graph." />
            )}
            <div className="mm-kpi-divider" />
            {/* infra group */}
            {live?.available_cpu_cores != null && (
              <KpiCard label="CPU Cores" value={String(live.available_cpu_cores)} accent="blue"
                tip="Number of CPU cores available to the JVM running this Neo4j instance." />
            )}
            {sysInfo?.free_physical_memory_bytes != null && (
              <KpiCard
                label="Free RAM"
                {...fmtBytes(sysInfo.free_physical_memory_bytes)}
                sub={sysInfo.total_physical_memory_bytes != null ? `of ${fmtBytesStr(sysInfo.total_physical_memory_bytes)} total` : undefined}
                accent="blue"
                tip="Currently unused physical RAM on the host. Low free RAM means the OS may start swapping, causing Neo4j slowdowns."
              />
            )}
            {storeTotal != null && (
              <KpiCard label="Store Size" {...fmtBytes(storeTotal)} sub="on disk" accent="blue"
                tip="Total on-disk footprint of all Neo4j store files (nodes, relationships, properties, strings, arrays)." />
            )}
            <div className="mm-kpi-divider" />
            {/* perf group */}
            {pcHit != null && (
              <KpiCard
                label="Cache Hit"
                value={`${(pcHit * 100).toFixed(1)}%`}
                sub={pcUsage != null ? `${(pcUsage * 100).toFixed(0)}% full` : undefined}
                accent={pcHit < 0.95 ? 'orange' : 'green'}
                tip="Page cache hit ratio — % of data reads served from RAM instead of disk. Below 95% means frequent disk I/O."
              />
            )}
            <div className="mm-kpi-divider" />
            {/* db group */}
            <KpiCard
              label="Indexes"
              value={String(data?.indexes?.summary.total ?? idxItems.length)}
              sub={`${data?.indexes?.summary.online ?? 0} online`}
              accent="green"
              tip="Number of indexes defined on this database. Only ONLINE indexes serve queries; POPULATING ones are still being built."
            />
            <KpiCard
              label="Active Tx"
              value={String(txTotal)}
              sub={txTotal > 0 ? `${txTotal} running` : undefined}
              accent={txTotal > 5 ? 'orange' : 'green'}
              tip="Number of currently open transactions. High counts may indicate long-running queries or lock contention."
            />
            <KpiCard
              label="GDS Graphs"
              value={String(gdsGraphs.length)}
              sub={gdsGraphs.length > 0 ? `${gdsGraphs.length} in memory` : undefined}
              accent={gdsGraphs.length > 0 ? 'purple' : 'green'}
              tip="Number of GDS in-memory projected graphs. Each consumes heap proportional to node/relationship count."
            />
          </div>

          {/* ── Row 2: Memory + Graph panels ── */}
          <div className="mm-mid-row">
            <MemoryPanel live={live} heap={heap} sysInfo={sysInfo} pageCache={data?.page_cache} />
            <GraphPanel graph={graph} />
          </div>

          {/* ── Row 3: Tabbed bottom panel ── */}
          <BottomPanel
            txPayload={data?.transactions}
            indexesPayload={data?.indexes}
            gdsPayload={data?.gds_jobs}
            gdsGraphs={data?.gds_graphs}
            queryLog={data?.query_log}
            tab={bottomTab}
            setTab={setBottomTab}
            onDropGraph={async (graphName: string) => {
              await apiService.executeSynchronousQuery(
                kgId,
                versionId,
                `CALL gds.graph.drop('${graphName.replace(/'/g, "\\'")}') YIELD graphName RETURN graphName`,
                selectedInstance
              );
              await fetchMetrics(true);
            }}
          />

        </div>
      )}
    </div>
  );
};

export default MemoryManager;
